/**
 * ArticleForm
 * Create/edit form for a Help article. Mirrors the RHF pattern used by
 * NotificationEditor (register/Controller, inline field errors, submit button
 * driven by parent modal footer via a forwarded form ref).
 */
import React, { useEffect } from 'react';
import { useForm, Controller, type UseFormSetError } from 'react-hook-form';
import { Input, Select } from '@components/common';
import type { SelectOption } from '@components/common';
import RichTextEditor from './RichTextEditor';
import type { HelpArticle, CreateHelpArticlePayload, UpdateHelpArticlePayload } from '@app-types';
import styles from './HelpForm.module.css';

export interface ArticleFormCategoryOption {
  id: number;
  name: string;
}

export interface ArticleFormValues {
  title: string;
  categoryId: string;
  sortOrder: number;
  body: string;
}

export interface ArticleFormProps {
  article?: HelpArticle;
  categories: ArticleFormCategoryOption[];
  defaultSortOrder: number;
  onSubmit: (data: CreateHelpArticlePayload | UpdateHelpArticlePayload) => void;
  formRef?: React.Ref<HTMLFormElement>;
  isSubmitting?: boolean;
  /**
   * Receives the form's setError so the parent can bind server-side failures
   * (e.g. a 400 rejecting an empty body) to the offending field instead of
   * only surfacing them as a toast.
   */
  onSetErrorReady?: (setError: UseFormSetError<ArticleFormValues>) => void;
}

const TITLE_MAX_LENGTH = 200;

/**
 * Returns true when the HTML body has neither visible text nor an Article
 * Image, mirroring the backend's isEmptyArticleBody - an image-only body is a
 * valid article. Parsing (rather than a strip-tags regex) keeps
 * content-bearing markup from being flattened away and decodes entities, so
 * `&amp;` counts as content. Non-breaking spaces are normalized to plain
 * spaces first: `<p>&nbsp;</p>` is still empty for our purposes.
 */
const isBodyEmpty = (html: string): boolean => {
  const body = new DOMParser().parseFromString(html, 'text/html').body;
  if (body.querySelector('img[data-image-id]')) return false;
  const text = body.textContent ?? '';
  return text.replace(/\u00a0/g, ' ').trim().length === 0;
};

const ArticleForm: React.FC<ArticleFormProps> = ({
  article,
  categories,
  defaultSortOrder,
  onSubmit,
  formRef,
  isSubmitting = false,
  onSetErrorReady
}) => {
  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors }
  } = useForm<ArticleFormValues>({
    mode: 'onChange',
    defaultValues: {
      title: article?.title ?? '',
      categoryId: article ? String(article.category_id) : '',
      sortOrder: article?.sort_order ?? defaultSortOrder,
      body: article?.body ?? ''
    }
  });

  useEffect(() => {
    reset({
      title: article?.title ?? '',
      categoryId: article ? String(article.category_id) : '',
      sortOrder: article?.sort_order ?? defaultSortOrder,
      body: article?.body ?? ''
    });
  }, [article, defaultSortOrder, reset]);

  useEffect(() => {
    onSetErrorReady?.(setError);
  }, [onSetErrorReady, setError]);

  const categoryOptions: SelectOption[] = categories.map((category) => ({
    value: String(category.id),
    label: category.name
  }));

  const submitHandler = handleSubmit((data) => {
    onSubmit({
      title: data.title.trim(),
      body: data.body,
      categoryId: Number(data.categoryId),
      sortOrder: data.sortOrder
    });
  });

  return (
    <form ref={formRef} onSubmit={submitHandler} className={styles.form} data-test-id="article-form">
      <Input
        label="Title"
        required
        error={errors.title?.message}
        dataTestId="article-form-title"
        {...register('title', {
          required: 'Title is required',
          maxLength: {
            value: TITLE_MAX_LENGTH,
            message: `Title must be at most ${TITLE_MAX_LENGTH} characters`
          }
        })}
      />

      <Controller
        control={control}
        name="categoryId"
        rules={{ required: 'Category is required' }}
        render={({ field }) => (
          <Select
            label="Category"
            required
            options={categoryOptions}
            value={field.value}
            onChange={field.onChange}
            error={errors.categoryId?.message}
            dataTestId="article-form-category"
          />
        )}
      />

      <Input
        type="number"
        label="Sort Order"
        required
        error={errors.sortOrder?.message}
        dataTestId="article-form-sort-order"
        {...register('sortOrder', {
          valueAsNumber: true,
          validate: {
            required: (v) => (v !== undefined && v !== null && !Number.isNaN(v)) || 'Sort order is required',
            integer: (v) => Number.isNaN(v) || Number.isInteger(v) || 'Must be a whole number'
          }
        })}
      />

      <Controller
        control={control}
        name="body"
        rules={{
          validate: (value) => !isBodyEmpty(value) || 'Body cannot be empty'
        }}
        render={({ field }) => (
          <RichTextEditor
            label="Body"
            required
            value={field.value}
            onChange={field.onChange}
            error={errors.body?.message}
            dataTestId="article-form-body"
          />
        )}
      />
    </form>
  );
};

export default ArticleForm;
